import { fullName, resolveRowId } from '../utils/tableSort';

/** Shared getters for user/coach/manager/student list rows */
export const userListSortColumns = {
    id: (r) => resolveRowId(r),
    name: (r) => fullName(r),
    username: (r) => r.username ?? r.userName ?? '',
    email: (r) => r.email ?? '',
    phone: (r) => r.phoneNumber ?? r.phone ?? '',
    status: (r) => (r.active === false ? 1 : 0),
};

export const branchListSortColumns = {
    id: (r) => resolveRowId(r) ?? r.branchId,
    name: (r) => r.name ?? '',
    code: (r) => r.branchCode ?? '',
    phone: (r) => r.phoneNumber ?? '',
    mode: (r) => r.mode ?? '',
    manager: (r) => r.managerName ?? '',
    status: (r) => (r.active === false ? 1 : 0),
};

export const levelListSortColumns = {
    id: (r) => r.levelId ?? r.id,
    name: (r) => r.name ?? '',
    branch: (r) => r.branchName ?? '',
    status: (r) => (r.active === false ? 1 : 0),
};

export const enrollmentListSortColumns = {
    id: (r) => r.enrollmentId ?? r.id,
    student: (r) => r.studentName ?? fullName(r),
    batch: (r) => r.batchName ?? '',
    level: (r) => r.levelName ?? '',
    chargesMode: (r) => (r.overrideChargesMode ?? r.chargesMode ?? ''),
    periodChargeStatus: (r) => r.periodChargeStatus ?? '',
    enrolledDate: (r) => r.enrolledDate ?? r.createdAt ?? r.created ?? '',
    discount: (r) => r.appliedDiscountName ?? '',
    endDate: (r) => r.endEnrollmentDate ?? r.end_enrollment_date ?? '',
    status: (r) => (r.active === false ? 1 : 0),
};

export const discountListSortColumns = {
    id: (r) => resolveRowId(r),
    code: (r) => r.code ?? '',
    name: (r) => r.name ?? '',
    value: (r) => Number(r.discountValue) || 0,
    scope: (r) => r.branchId ?? r.levelId ?? r.batchId ?? 'global',
    status: (r) => (r.isActive === false ? 1 : 0),
};

export const feePlanSortColumns = {
    id: (r) => r.planId ?? r.id,
    branch: (r) => r.branchId ?? '',
    code: (r) => r.code ?? '',
    planType: (r) => r.planType ?? '',
    groupType: (r) => r.groupType ?? '',
    rate: (r) => Number(r.defaultRate) || 0,
    credits: (r) => Number(r.defaultCredits) || 0,
    status: (r) => (r.active === false ? 1 : 0),
};

export const holidaySortColumns = {
    id: (r) => resolveRowId(r),
    name: (r) => r.name ?? '',
    from: (r) => r.startDate ?? '',
    to: (r) => r.endDate ?? '',
    branch: (r) => r.branchId ?? '',
};

export const atRiskSortColumns = {
    id: (r) => r.studentId,
    student: (r) => fullName(r),
    risk: (r) => Number(r.attendanceRate) || 0,
    rate: (r) => Number(r.attendanceRate) || 0,
    sessions: (r) => Number(r.sessionsAttended) || 0,
    lastSeen: (r) => r.lastSeenIso ?? '',
    batches: (r) => r.batchNames ?? '',
};

export const batchListSortColumns = {
    id: (r) => r.batchId ?? r.id,
};

/** Student portal — enrollments list */
export const studentEnrollmentSortColumns = {
    id: (r) => r.enrollmentId ?? r.id,
    level: (r) => r.levelName ?? '',
    batch: (r) => r.batchName ?? '',
    enrolledDate: (r) => r.enrolledDate ?? '',
};

/** Student portal — attendance history (default: date desc) */
export const studentAttendanceSortColumns = {
    id: (r) => r.sessionId ?? r.id,
    date: (r) => r.startTime ?? r.sessionStart ?? '',
    when: (r) => r.startTime ?? r.sessionStart ?? '',
    status: (r) => r.attendanceType ?? r.status ?? '',
    level: (r) => r.levelName ?? '',
    batch: (r) => r.batchName ?? '',
    coach: (r) => r.coachName ?? '',
};

export const paymentsDueSortColumns = {
    id: (r) => r.studentId,
    student: (r) => r.studentName ?? '',
    status: (r) => r.status ?? '',
    balance: (r) => Number(r.balance) || 0,
    branch: (r) => r.branchName ?? '',
    lastPayment: (r) => r.lastTopupDate ?? '',
};

export const studentAttendanceSummarySortColumns = {
    id: (r) => r.studentId,
    student: (r) => r.studentName ?? '',
    rate: (r) => Number(r.attendancePercent) || 0,
    sessions: (r) => Number(r.totalSessions) || 0,
    branch: (r) => r.branchName ?? '',
    batch: (r) => r.batchName ?? '',
    level: (r) => r.levelName ?? '',
    coach: (r) => r.coachName ?? '',
};

export const academyRevenueMonthlySortColumns = {
    id: (r) => r.sortKey ?? r.month ?? '',
    month: (r) => r.sortKey ?? r.month ?? '',
    sessions: (r) => Number(r.sessionCount) || 0,
    gross: (r) => Number(r.gross) || 0,
    academyRevenue: (r) => Number(r.academyRevenue) || 0,
    coachPayouts: (r) => Number(r.coachPayouts) || 0,
    walletTopUps: (r) => Number(r.walletTopUps) || 0,
};

export const academyRevenueBranchSortColumns = {
    id: (r) => r.branchId,
    branch: (r) => r.branchName ?? '',
    sessions: (r) => Number(r.sessionCount) || 0,
    gross: (r) => Number(r.gross) || 0,
    academyRevenue: (r) => Number(r.academyRevenue) || 0,
    coachPayouts: (r) => Number(r.coachPayouts) || 0,
};

export const sessionStatementWalletSortColumns = {
    date: (r) => r.date ?? '',
    description: (r) => r.description ?? '',
    amount: (r) => Number(r.amount) || 0,
    balanceAfter: (r) => Number(r.balanceAfter) || 0,
};

export const sessionStatementSessionSortColumns = {
    dateTime: (r) => r.dateTime ?? '',
    batch: (r) => r.batch ?? '',
    topics: (r) => r.sessionTopics ?? '',
    status: (r) => r.attendance ?? '',
    balanceBefore: (r) => Number(r.balanceBefore) || 0,
    charges: (r) => Number(r.charges) || 0,
    balanceAfter: (r) => Number(r.balanceAfter) || 0,
};

/** Admin — Sessions Summary report */
export const sessionsSummarySortColumns = {
    start: (r) => r.startTime ?? '',
    level: (r) => r.levelName ?? '',
    batch: (r) => r.batchName ?? '',
    coach: (r) => r.coachName ?? '',
    topic: (r) => r.sessionTopic ?? '',
};
